import { Box, Text } from '@wix/design-system';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Experiments, MediaDevices, Theme } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import { useBreakpoint } from 'providers/BreakpointProvider';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import FourteenDaysIcon from '../../assets/images/page-footer/14-days-icon.svg';
import CreditIcon from '../../assets/images/page-footer/get-credit-icon.svg';

export interface MoneyBackGuaranteeProps extends InjectedExperimentsProps {
  rootStore: RootStore;
}

const ICON_SIZE_DESKTOP = {
  width: '41px',
  height: '38px',
  flexShrink: 0,
} as const;
const ICON_SIZE_MOBILE = {
  width: '20px',
  height: '20px',
  flexShrink: 0,
} as const;
const CREDIT_ICON_SIZE_DESKTOP = {
  width: '52px',
  height: '38px',
  flexShrink: 0,
} as const;
const CREDIT_ICON_SIZE_MOBILE = {
  width: '24px',
  height: '24px',
  flexShrink: 0,
} as const;

const themedDescKey = {
  [Theme.AdvancedPlans]: 'page_footer.try_14_days',
  [Theme.WixStudio]: 'studio_package_picker.footer.try_wix_studio_free',
  [Theme.EditorX]: 'page_footer.try_14_days_editorx',
  [Theme.Dayful]: 'page_footer.try_14_days_dayful',
};

const mobileTitleKey = {
  default: 'page_footer.money_back_guarantee.non_desktop',
  secondUpgrade: 'page_footer.money_back_guarantee_second_upgrade.non_desktop',
};

const MoneyBackGuaranteeComp: React.FunctionComponent<
  MoneyBackGuaranteeProps
> = ({ rootStore, experiments }) => {
  const {
    isSecondUpgrade,
    isSepaCountry,
    appContext: {
      pageMode: { theme },
    },
  } = rootStore.appStore;

  const { t } = useTranslation();
  const { currentBreakpoint } = useBreakpoint();
  const isMobile = currentBreakpoint === MediaDevices.Mobile;

  const shouldShowSepaDisclaimer =
    isSepaCountry && !experiments.enabled(Experiments.MoneyBackGuaranteeInSepa);

  const descKey = isSecondUpgrade
    ? 'app.second_upgrade_text'
    : shouldShowSepaDisclaimer
    ? 'page_footer.try_14_days_sepa'
    : themedDescKey[theme];

  const titleKey = isMobile
    ? isSecondUpgrade
      ? mobileTitleKey.secondUpgrade
      : mobileTitleKey.default
    : isSecondUpgrade
    ? 'page_footer.money_back_guarantee_second_upgrade'
    : 'page_footer.money_back_guarantee';

  const iconSize = isSecondUpgrade
    ? isMobile
      ? CREDIT_ICON_SIZE_MOBILE
      : CREDIT_ICON_SIZE_DESKTOP
    : isMobile
    ? ICON_SIZE_MOBILE
    : ICON_SIZE_DESKTOP;

  const Icon = isSecondUpgrade ? CreditIcon : FourteenDaysIcon;

  return (
    <Box
      direction={isMobile ? 'vertical' : 'horizontal'}
      align={isMobile ? 'center' : undefined}
      dataHook='money-back'
      verticalAlign={isMobile ? undefined : 'middle'}
      gap={isMobile ? 'SP1' : 'SP3'}
    >
      <Box
        direction='horizontal'
        verticalAlign='middle'
        gap={isMobile ? 'SP1' : 'SP3'}
      >
        <Box {...iconSize}>
          <Icon width='100%' height='100%' />
        </Box>
        <Box direction='vertical'>
          <Text weight='bold' size='tiny' tagName={isMobile ? undefined : 'p'}>
            {t(titleKey)}
          </Text>
          {!isMobile && (
            <Box marginTop='10px'>
              <Text
                weight='thin'
                size='tiny'
                data-testid={`money-back-${theme}`}
              >
                {t(descKey)}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
      {isMobile && (
        <Box textAlign='center'>
          <Text weight='thin' size='tiny' data-testid={`money-back-${theme}`}>
            {t(descKey)}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export const MoneyBackGuarantee = withInjectedRootStore(
  withExperiments(MoneyBackGuaranteeComp),
);
